import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import GuestInput from "../common/input/guestInput";
import SelectItem from "../common/input/selectItem";
import { Staff } from "@/graphql/types";
import { errorMessageList, validations } from ".";
import { SpecialGroupDetail, GuestInfo } from "./index.d";
import { useQuery } from "react-query";
import Button from "../common/button/button";
import { isMobile } from "@/lib/ts/device";
import LoadingIcon from "../common/loadingIcon";
import SelectItemV2 from "../common/input/selectItemV2";
import { InputLabel } from "@mui/material";

type Props = {
  lineId?: string;
  name?: string;
  phonetic?: string;
  email?: string;
  note?: string;
  groupSize?: number;
  invitationId?: number;

  specialGroupDetails?: SpecialGroupDetail[];

  // falseなら招待客選択欄を表示しない
  activateInvitation?: boolean;

  staffList?: Staff[];
  isVip?: boolean;

  onSubmit: (value: GuestInfo) => any;
};

const GuestInformation = (props: Props) => {
  const [loading, setLoading] = useState(false)
  // inputの値
  const [name, setName] = useState(props.name);
  const [phonetic, setPhonetic] = useState(props.phonetic);
  const [email, setEmail] = useState(props.email);
  const [note, setNote] = useState(props.note);
  const [groupSize, setGroupSize] = useState(
    props.groupSize ? props.groupSize.toString() : "",
  );

  // 学生・子どもなど、一般以外の区分
  const [specialGroupSizes, setSpecialGroupSizes] = useState([] as number[]);

  const [invitationId, setInvitationId] = useState(props.invitationId || 0);

  // 各inputにerrorが生じているか
  const [nameError, setNameError] = useState(false);
  const [phoneticError, setPhoneticError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [noteError, setNoteError] = useState(false);
  const [groupSizeError, setGroupSizeError] = useState(false);

  const [specialGroupSizesError, setSpecialGroupSizesError] = useState(
    [] as boolean[],
  );
  // const [invitationIdError, setInvitationIdError] = useState(false)

  useQuery([props.specialGroupDetails], () => {
    setLoading(true)
    setSpecialGroupSizes(
      props.specialGroupDetails
        ? props.specialGroupDetails.map((detail) => detail.size)
        : [],
    );
    setSpecialGroupSizesError(
      props.specialGroupDetails
        ? props.specialGroupDetails.map((detail) => false)
        : [],
    );
    setLoading(false)
  });

  const onInputChange = useCallback(
    (
      value: string,
      errorOccurs: boolean,
      setFunction: any,
      setErrorFunction: any,
    ) => {
      setFunction(value);
      setErrorFunction(errorOccurs);
    },
    [],
  );

  const onSpecialGroupSizeChange = useCallback(
    (value: string, errorOccurs: boolean, index: number) => {
      const specialGroupSizesCopy = [...specialGroupSizes];
      const specialGroupSizesErrorCopy = [...specialGroupSizesError];
      specialGroupSizesCopy[index] = parseInt(value);
      specialGroupSizesErrorCopy[index] = errorOccurs;

      setSpecialGroupSizes(specialGroupSizesCopy);
      setSpecialGroupSizesError(specialGroupSizesErrorCopy);
    },
    [specialGroupSizes, specialGroupSizesError],
  );

  const totalGroupSizeInvalidate = useMemo(() => {
    let totalGroupSize = parseInt(groupSize) || 0;
    specialGroupSizes.forEach((specialGroupSize) => {
      totalGroupSize += specialGroupSize;
    });
    return totalGroupSize <= 0 || totalGroupSize > 10;
  }, [groupSize, specialGroupSizes]);

  const errorMessages = useMemo(() => {
    const errorMessages: string[] = [];
    if (nameError) {
      errorMessages.push(errorMessageList().name);
    }
    if (phoneticError) {
      errorMessages.push(errorMessageList().phonetic);
    }
    if (emailError) {
      errorMessages.push(errorMessageList().email);
    }
    if (noteError) {
      errorMessages.push(errorMessageList().note);
    }
    if (groupSizeError) {
      errorMessages.push(errorMessageList().groupSize);
    }
    if (specialGroupSizesError.length > 0) {
      specialGroupSizesError.forEach((value, index) => {
        if (
          value &&
          props.specialGroupDetails &&
          props.specialGroupDetails[index]
        ) {
          const name = props.specialGroupDetails[index].name;
          const message = errorMessageList(name).specialGroupSize;
          console.log(message);
          errorMessages.push(message);
        }
      });
    }

    return errorMessages;
  }, [
    emailError,
    groupSizeError,
    nameError,
    noteError,
    phoneticError,
    props.specialGroupDetails,
    specialGroupSizesError,
  ]);

  const selectedStaffIndex = useMemo(() => {
    if(props.staffList) {
      return props.staffList.findIndex(s => s.id === invitationId)
    }
    return -1
  }, [invitationId, props.staffList])

  const onSubmit = useCallback(() => {
    if (errorMessages.length > 0) {
      return;
    } else {
      if (!name || !phonetic || !email || !groupSize || selectedStaffIndex === -1) {
        return;
      }
      props.onSubmit({
        name,
        phonetic,
        email,
        note,
        groupSize: parseInt(groupSize),
        specialGroupSizes,
        invitationId,
      });
    }
  }, [email, errorMessages.length, groupSize, invitationId, name, note, phonetic, props, selectedStaffIndex, specialGroupSizes]);



  const onStaffSelected = useCallback((staff: Staff, index: number) => {
    if (staff) {
      console.log(staff.id)
      setInvitationId((staff.id !== null && staff.id !== undefined) ? staff.id : -1);
    }
  }, [])

  const component = useMemo(() => {
    return (
      <GuestInformationStyle>
        <GuestInput
          id="guest-info-name"
          label="氏名"
          defaultValue={props.name || ""}
          placeholder="魔法太郎"
          onChange={(value: string, errorOccurs: boolean) => {
            onInputChange(value, errorOccurs, setName, setNameError);
          }}
          errorMessage={errorMessageList().name}
          literalValidation={validations.name}
          error={nameError}
        />
        <GuestInput
          id="guest-info-phonetic"
          label="ふりがな"
          defaultValue={props.phonetic || ""}
          placeholder="まほうたろう"
          onChange={(value: string, errorOccurs: boolean) => {
            onInputChange(value, errorOccurs, setPhonetic, setPhoneticError);
          }}
          errorMessage={errorMessageList().phonetic}
          literalValidation={validations.phonetic}
          error={phoneticError}
        />
        <GuestInput
          id="guest-info-email"
          label="メールアドレス"
          defaultValue={props.email || ""}
          placeholder="magic.one.ahead@gmail.com"
          onChange={(value: string, errorOccurs: boolean) => {
            onInputChange(value, errorOccurs, setEmail, setEmailError);
          }}
          errorMessage={errorMessageList().email}
          literalValidation={validations.email}
          error={emailError}
        />
        <GuestInput
          id="guest-info-group-size"
          label="人数(一般)"
          defaultValue={props.groupSize ? props.groupSize.toString() : "0"}
          placeholder="2"
          onChange={(value: string, errorOccurs: boolean) => {
            onInputChange(value, errorOccurs, setGroupSize, setGroupSizeError);
          }}
          errorMessage={!props.isVip? errorMessageList().groupSize : "座席指定は1人分のみ可能です。"}
          extraInvalidDefinition={totalGroupSizeInvalidate}
          literalValidation={validations.groupSize}
          numeralValidation={props.isVip? {max: 1, min: 1} : undefined}
          error={groupSizeError}
        />
        {props.specialGroupDetails && !props.isVip ? (
          props.specialGroupDetails.map((detail, index) => {
            return (
              <GuestInput
                id={`guest-info-group-size-${index}`}
                key={"specialGroup" + index}
                label={`人数（${detail.name}）`}
                defaultValue={detail.size.toString()}
                placeholder="2"
                onChange={(value: string, errorOccurs: boolean) =>
                  onSpecialGroupSizeChange(value, errorOccurs, index)
                }
                errorMessage={
                  errorMessageList(
                    props.specialGroupDetails
                      ? props.specialGroupDetails[index].name
                      : "",
                  ).specialGroupSize
                }
                extraInvalidDefinition={totalGroupSizeInvalidate}
                literalValidation={validations.specialGroupSize}
                error={specialGroupSizesError[index]}
              />
            );
          })
        ) : (
          <></>
        )}
        {props.activateInvitation &&
        props.staffList &&
        props.staffList.length ? (
          <div className="select-staff">
            <InputLabel>招待者</InputLabel>
            <SelectItemV2 
              items={props.staffList}
              selectedIndex={selectedStaffIndex}
              changeIndex={onStaffSelected}
              width={250}
            />
          </div>
        ) : (
          <></>
        )}
        <GuestInput
          id="guest-info-note"
          label="備考・特記事項"
          defaultValue=""
          placeholder=""
          onChange={(value: string, errorOccurs: boolean) => {
            onInputChange(value, errorOccurs, setNote, setNoteError);
          }}
          multiLine
          outlined
          errorMessage={errorMessageList().note}
          error={noteError}
          literalValidation={validations.note}
        />
        <div className="submit">
          <Button
            onClick={onSubmit}
            disabled={errorMessages.length > 0 || totalGroupSizeInvalidate || selectedStaffIndex === -1}
            label="次へ"
            buttonType={isMobile() ? "default-red" : "powerful-red"}
          />
        </div>
        {loading ? <LoadingIcon /> : <></>}
      </GuestInformationStyle>
    );
  }, [emailError, errorMessages.length, groupSizeError, loading, nameError, noteError, onInputChange, onSpecialGroupSizeChange, onStaffSelected, onSubmit, phoneticError, props.activateInvitation, props.email, props.groupSize, props.isVip, props.name, props.phonetic, props.specialGroupDetails, props.staffList, selectedStaffIndex, specialGroupSizesError, totalGroupSizeInvalidate]);

  return component;
};

export default GuestInformation;

const GuestInformationStyle = styled.div`
  width: 90%;
  background-color: white;
  margin: 10px auto 10px auto;
  text-align: center;
  padding: 20px 0 20px 0;
  border-radius: 8px;
  .submit {
    text-align: center;
    margin: auto;
  }
  .select-staff {
    width: 250px;
    margin: auto;
  }
`;
